import { Context, Controller } from '@hotwired/stimulus';

/**
 * Dynamically changes the site access input when options are updated.
 */
export default class EmployeesSiteAccessController extends Controller {
  public static targets = ['submit', 'switch'];

  public static values = {
    'confirm-dialog-message': String,
    'confirm-dialog-id': String,
  };

  private initialValues: Record<string, string | boolean> = {};
  private changedValues: Record<string, string | boolean> = {};

  declare private confirmDialogMessageValue: string;
  declare private confirmDialogIdValue: string;

  declare private submitTargets: readonly HTMLButtonElement[];
  declare private switchTargets: readonly HTMLAnchorElement[];
  private form!: HTMLFormElement;

  private pendingTimeout: ReturnType<typeof setTimeout> | undefined;
  private pendingCleanupFunction: (() => unknown) | undefined;

  constructor(context: Context) {
    super(context);

    this.onFreezeForm = this.onFreezeForm.bind(this);
    this.onRestoreForm = this.onRestoreForm.bind(this);
    this.onResetInitial = this.onResetInitial.bind(this);
    this.onBlinkSuccess = this.onBlinkSuccess.bind(this);
  }

  private get fields(): NodeListOf<HTMLSelectElement> {
    return this.element.querySelectorAll(
      'select[data-label][data-distinguished-name]',
    );
  }

  private get checks(): NodeListOf<HTMLInputElement> {
    return this.element.querySelectorAll('input[type="checkbox"][data-toggle]');
  }

  private get radios(): NodeListOf<HTMLInputElement> {
    return this.element.querySelectorAll('input[type="radio"][data-track]');
  }

  public connect(): void {
    this.onResetInitial();
    this.update();

    this.form = this.element.closest('form')!;
    this.form.addEventListener('ajax:before', this.onFreezeForm);
    this.form.addEventListener('ajax:success', this.onResetInitial);
    this.form.addEventListener('ajax:success', this.onBlinkSuccess);
    this.form.addEventListener('ajax:complete', this.onRestoreForm);
  }

  public disconnect(): void {
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);
    }

    if (this.pendingCleanupFunction) {
      this.pendingCleanupFunction();
    }

    this.element.querySelectorAll('[value="-"]').forEach((option) => {
      option.remove();
    });

    this.form.removeEventListener('ajax:before', this.onFreezeForm);
    this.form.removeEventListener('ajax:success', this.onResetInitial);
    this.form.removeEventListener('ajax:success', this.onBlinkSuccess);
    this.form.removeEventListener('ajax:complete', this.onRestoreForm);
  }

  private onFreezeForm() {
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);
    }

    if (this.pendingCleanupFunction) {
      this.pendingCleanupFunction();
    }

    this.fields.forEach((field) => {
      if (this.changedValues[field.name] === undefined) {
        field.setAttribute('disabled', '');
      }
    });
  }

  private onRestoreForm() {
    this.fields.forEach((field) => {
      field.removeAttribute('disabled');
    });

    this.update();
  }

  private onBlinkSuccess() {
    // Run in next frame so the button is restored from its disabled state;
    requestAnimationFrame(() => {
      const restoreNeutral =
        this.submitTargets[0].classList.contains('--neutral');
      const restorePrimary =
        this.submitTargets[0].classList.contains('--primary');
      const restoreText = this.submitTargets[0].textContent;

      this.pendingCleanupFunction = () => {
        this.submitTargets.forEach((target) => {
          target.textContent = restoreText;
          target.classList.remove('--green');
          target.classList.toggle('--neutral', restoreNeutral);
          target.classList.toggle('--primary', restorePrimary);
        });

        this.pendingCleanupFunction = undefined;
      };

      this.submitTargets.forEach((target) => {
        target.classList.remove('--neutral', '--primary');
        target.classList.add('--green');
        target.textContent = 'Changes applied!';
      });

      this.pendingTimeout = setTimeout(this.pendingCleanupFunction, 3000);
    });
  }

  private onResetInitial() {
    this.initialValues = {};
    this.changedValues = {};

    this.fields.forEach((field) => {
      const { field: valueField, value } = this.valueOf(field);
      if (field === valueField) {
        this.initialValues[field.name] = value;
      }
    });

    this.checks.forEach((field) => {
      const { checked } = this.checkedState(field);
      this.initialValues[field.id] = checked;
    });

    this.radios.forEach((field) => {
      const { checked } = this.checkedState(field);

      if (checked) {
        this.initialValues[field.name] = field.value;
      }
    });
  }

  private fieldFor(name: string | undefined): HTMLSelectElement | null {
    if (!name) {
      return null;
    }

    return this.element.querySelector<HTMLSelectElement>(
      `select[data-distinguished-name="${name}"]`,
    );
  }

  private valueOf(field: HTMLSelectElement): {
    field: HTMLSelectElement;
    value: string;
  } {
    if (field.value && field.value !== '-') {
      return { field, value: field.value };
    }

    const dn = field.getAttribute('data-distinguished-name') ?? '';
    const parent = dn.split('.').slice(0, -1).join('.');

    const parentField = this.fieldFor(parent);
    if (!parentField) {
      return { field, value: '' };
    }

    const parentValue = this.valueOf(parentField);
    if (dn.endsWith('.employees') || dn.includes('.employees.')) {
      if (
        parentValue.value !== 'manager' &&
        parentValue.value !== 'administrator'
      ) {
        return { field: parentValue.field, value: '' };
      }
    }

    return parentValue;
  }

  private checkedState(field: HTMLInputElement): {
    field: HTMLInputElement;
    checked: boolean;
  } {
    return { field, checked: field.checked };
  }

  private update(): void {
    this.fields.forEach((field) => {
      const { field: valueField, value } = this.valueOf(field);

      // Remove any disabled field
      field.querySelector('[value="-"]')?.remove();

      if (valueField === field) {
        const fieldChanged = this.initialValues[field.name] !== value;

        if (fieldChanged) {
          this.changedValues[field.name] = value;
        } else {
          delete this.changedValues[field.name];
        }
      } else {
        if (this.initialValues[field.name] !== undefined) {
          this.changedValues[field.name] = value;
        } else {
          delete this.changedValues[field.name];
        }

        if (value === '') {
          field.selectedIndex = 0;
        } else {
          const disabledNode = document.createElement('option');
          disabledNode.setAttribute('value', '-');
          disabledNode.selected = true;

          field.prepend(disabledNode);
          disabledNode.textContent = `${value} → ${valueField.getAttribute(
            'data-label',
          )}`;
        }
      }
    });

    this.checks.forEach((field) => {
      const { checked } = this.checkedState(field);
      const fieldChanged = this.initialValues[field.id] !== checked;

      if (fieldChanged) {
        this.changedValues[field.id] = checked;
      } else {
        delete this.changedValues[field.id];
      }
    });

    this.radios.forEach((field) => {
      const { checked } = this.checkedState(field);
      if (!checked) {
        return;
      }

      const fieldChanged = this.initialValues[field.name] !== field.value;

      if (fieldChanged) {
        this.changedValues[field.name] = field.value;
      } else {
        delete this.changedValues[field.name];
      }
    });

    const hasChanges = Boolean(Object.keys(this.changedValues).length);
    const hadChanges = this.submitTargets[0].classList.contains('--primary');

    this.submitTargets.forEach((target) => {
      target.classList.toggle('--neutral', !hasChanges);
      target.classList.toggle('--primary', hasChanges);
    });

    if (hasChanges != hadChanges) {
      this.switchTargets.forEach((target) => {
        if (hasChanges) {
          target.setAttribute('data-confirm', this.confirmDialogMessageValue);
          target.setAttribute('data-confirm-dialog', this.confirmDialogIdValue);
        } else {
          target.removeAttribute('data-confirm');
          target.removeAttribute('data-confirm-dialog');
        }
      });
    }
  }

  public onChange(): void {
    this.update();
  }

  public onToggle(): void {
    this.update();
  }
}
